'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback } from 'react'
import { useGetAllBlogsQuery } from '@/api/blogs/get-all-blogs'
import { useGetAllBlogTopicsQuery } from '@/api/blogs/get-all-topics'
import { FooterItem } from '@/features/footer/footer-item'
import { FooterSection } from '@/features/footer/footer-section'
import {
    getLanguagesForRegion,
    getLocaleForRegion,
    Language,
    Locale,
    parseLanguageFromLocale,
    parseRegionFromLocale,
    Region,
} from '@/setup/locales'
import { getPathname, usePathname } from '@/setup/routing'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'

export const FooterLanguageSection = () => {
    const router = useRouter()

    const locale = useLocale()
    const currentRegion = parseRegionFromLocale(locale)
    const currentLanguage = parseLanguageFromLocale(locale)

    const pathname = usePathname()
    const rawParams = useParams()
    const qc = useQueryClient()
    const searchParams = useSearchParams()

    const { data: blogs } = useGetAllBlogsQuery()

    const { data: topicsResponse } = useGetAllBlogTopicsQuery()

    const topics = topicsResponse?.data

    const onLocaleSwitch = useCallback(
        (nextLocale: Locale) => {
            let params =
                pathname === '/lp/monate-universal/[cityOrCountrySlug]/[a]/[[...customSlug]]' &&
                !rawParams.customSlug
                    ? { ...rawParams, customSlug: [] }
                    : rawParams

            // In order to properly update the params to the localized slug, we need to check it's locales property
            if (pathname.startsWith('/blog/')) {
                if (blogs?.data && topics) {
                    if (pathname.startsWith('/blog/topic/') && rawParams.slug) {
                        const localizedSlug = topics.find(
                            (topic) =>
                                typeof rawParams.slug === 'string' &&
                                Object.values(topic.locales).includes(rawParams.slug)
                        )

                        if (localizedSlug) {
                            params = {
                                ...params,
                                slug: localizedSlug.locales[parseLanguageFromLocale(nextLocale)],
                            }
                        }
                    } else if (pathname.startsWith('/blog/') && rawParams.slugId) {
                        const id =
                            typeof rawParams.slugId === 'string'
                                ? rawParams.slugId.split('-').pop()
                                : null

                        const localizedSlugId = blogs.data.find((blog) => blog.id === id)
                        const localizedSlug =
                            localizedSlugId?.slugs[parseLanguageFromLocale(nextLocale)]
                        const newParam = `${localizedSlug}-${localizedSlugId?.id}`
                        if (localizedSlugId) {
                            params = { ...params, slugId: newParam }
                        }
                    }
                }
            }

            const nextPathname = getPathname(
                pathname,
                // @ts-ignore
                params,
                nextLocale
            )

            const nextSearchParamsStr = searchParams.toString()

            const nextRoute = nextSearchParamsStr
                ? `${nextPathname}?${nextSearchParamsStr}`
                : nextPathname

            router.replace(nextRoute)

            qc.invalidateQueries()

            router.refresh()
        },
        [router, pathname, rawParams, searchParams, qc, blogs, topics]
    )

    const onRegionSwitch = useCallback(
        (nextRegion: Region) => {
            const nextLocale = getLocaleForRegion(nextRegion, currentLanguage as Language)

            onLocaleSwitch(nextLocale)
        },
        [currentLanguage, onLocaleSwitch]
    )

    const t = useTranslations()

    return (
        <FooterSection title={t('footer.country.title')}>
            {Object.values(Region).map((region) => {
                const regionLabel = run((): { flag: string; name: string } => {
                    switch (region) {
                        case Region.AT:
                            return { flag: '🇦🇹', name: t('footer.country.austria') }

                        case Region.DE:
                            return { flag: '🇩🇪', name: t('footer.country.germany') }

                        case Region.GB:
                            return { flag: '🇬🇧', name: t('footer.country.unitedKingdom') }

                        case Region.NL:
                            return { flag: '🇳🇱', name: t('footer.country.netherlands') }
                    }
                })

                return (
                    <div
                        key={'footer-language-section' + region}
                        className="flex flex-col gap-y-0.5"
                    >
                        <FooterItem
                            label={
                                <span className="flex flex-row items-center gap-x-s">
                                    <span className="w-xl shrink-0">{regionLabel.flag}</span>
                                    {regionLabel.name}
                                </span>
                            }
                            className={cn(region === currentRegion && 'font-bold text-fg-primary')}
                            onClick={() => onRegionSwitch(region)}
                        />

                        {/* pl-2xl = flag width (w-xl) + gap-x-s, so languages align with the country name */}
                        <div className="flex flex-row gap-x-m pl-2xl">
                            {getLanguagesForRegion(region).map((language) => {
                                const languageLabel = run((): string => {
                                    switch (language) {
                                        case Language.DE:
                                            return 'Deutsch'

                                        case Language.EN:
                                            return 'English'

                                        case Language.NL:
                                            return 'Nederlands'
                                    }
                                })

                                return (
                                    <FooterItem
                                        key={'footer-language-section' + region + language}
                                        label={languageLabel}
                                        className={cn(
                                            'text-p-sm text-fg-tertiary',
                                            region === currentRegion &&
                                                language === currentLanguage &&
                                                'font-bold text-fg-primary'
                                        )}
                                        onClick={() =>
                                            onLocaleSwitch(getLocaleForRegion(region, language))
                                        }
                                    />
                                )
                            })}
                        </div>
                    </div>
                )
            })}
        </FooterSection>
    )
}
